import React from 'react'
import { createPortal } from 'react-dom'
import { api } from '../api/client'
import type { NotePhoto } from '../types/app'
import { IconButton } from '../ui/Icon'
import { useDialog } from '../hooks/useDialog'
import { SHORT_MONTHS } from '../catalog/format'
import { saveErrorMessage } from '../screens/settings/toast'
import '../components/Sheet.css'
import './NotePhotoGrid.css'

// The photos are private, so the page can't point an <img> at their URL: it
// fetches each as a blob with the session and shows an object URL. The URL is
// revoked when the tile goes away or shows a different photo. Nothing is
// fetched until `enabled`, so a long notes list doesn't pull every photo.
function usePhotoBlob(clientId: string, noteId: string, photoId: string, enabled: boolean): { url: string | null; failed: boolean } {
  const [state, setState] = React.useState<{ url: string | null; failed: boolean }>({ url: null, failed: false })
  React.useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let made: string | null = null
    setState({ url: null, failed: false })
    api
      .notePhotoBlob(clientId, noteId, photoId)
      .then((blob) => {
        if (cancelled) return
        made = URL.createObjectURL(blob)
        setState({ url: made, failed: false })
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, failed: true })
      })
    return () => {
      cancelled = true
      if (made) URL.revokeObjectURL(made)
    }
  }, [clientId, noteId, photoId, enabled])
  return state
}

// useNearViewport turns true once the element comes within 200px of the
// viewport, and stays true. Each photo is a full-size download, so a client
// with years of notes would otherwise fetch them all on every profile open.
// Without IntersectionObserver (old browsers, jsdom) it is true at once.
function useNearViewport(ref: React.RefObject<Element | null>): boolean {
  const [near, setNear] = React.useState(() => typeof IntersectionObserver === 'undefined')
  React.useEffect(() => {
    if (near || !ref.current) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true)
      },
      { rootMargin: '200px' },
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [near, ref])
  return near
}

// "5 Oct": the day she took it, from the stored date so the time zone can't move it.
function addedLabel(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number)
  return `${d} ${SHORT_MONTHS[m - 1]}`
}

interface GridProps {
  clientId: string
  noteId: string
  photos: NotePhoto[]
  // Called once the API has deleted the photo, so the note can drop it.
  onRemoved: (photoId: string) => void
  // Tiles after the saved photos in the same grid: photos still going up.
  children?: React.ReactNode
}

// NotePhotoGrid shows an appointment note's photos as square thumbnails;
// tapping one opens it full size, with a way to remove it.
export default function NotePhotoGrid({ clientId, noteId, photos, onRemoved, children }: GridProps) {
  return (
    <ul className="note-photos">
      {photos.map((photo, i) => (
        <li key={photo.id}>
          <Thumb
            clientId={clientId}
            noteId={noteId}
            photo={photo}
            label={`Photo ${i + 1} of ${photos.length}, added ${addedLabel(photo.created_at)}`}
            onRemoved={onRemoved}
          />
        </li>
      ))}
      {children}
    </ul>
  )
}

function Thumb({ clientId, noteId, photo, label, onRemoved }: {
  clientId: string
  noteId: string
  photo: NotePhoto
  label: string
  onRemoved: (photoId: string) => void
}) {
  const tileRef = React.useRef<HTMLButtonElement>(null)
  const near = useNearViewport(tileRef)
  const [open, setOpen] = React.useState(false)
  // Opening the viewer loads the photo even if the tile never came near the
  // viewport; the viewer then shares the tile's blob.
  const { url, failed } = usePhotoBlob(clientId, noteId, photo.id, near || open)
  return (
    <>
      <button ref={tileRef} type="button" className="note-photos__tile" aria-label={label} onClick={() => setOpen(true)}>
        {url ? <img src={url} alt="" /> : failed ? <span className="note-photos__failed">Couldn’t load</span> : null}
      </button>
      {open && (
        <Viewer
          clientId={clientId}
          noteId={noteId}
          photo={photo}
          url={url}
          failed={failed}
          label={label}
          onClose={() => setOpen(false)}
          onRemoved={onRemoved}
        />
      )}
    </>
  )
}

function Viewer({ clientId, noteId, photo, url, failed, label, onClose, onRemoved }: {
  clientId: string
  noteId: string
  photo: NotePhoto
  url: string | null
  failed: boolean
  label: string
  onClose: () => void
  onRemoved: (photoId: string) => void
}) {
  const [confirming, setConfirming] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  const remove = async () => {
    setBusy(true)
    setError('')
    try {
      await api.removeNotePhoto(clientId, noteId, photo.id)
    } catch (e) {
      setError(saveErrorMessage(e))
      setBusy(false)
      return
    }
    // The tile unmounts with the photo, taking this viewer with it.
    onRemoved(photo.id)
  }

  const sheet = (
    <div className="sheet-overlay" onClick={(e) => { e.stopPropagation(); if (!busy) onClose() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="note-photos__viewer-head">
          <h2>{label.split(', ')[0]}</h2>
          <IconButton icon="close" label="Close" onClick={onClose} disabled={busy} />
        </div>
        <div className="note-photos__full" style={{ aspectRatio: `${photo.width} / ${photo.height}` }}>
          {url ? <img src={url} alt={label} /> : failed ? <span className="note-photos__failed">Couldn’t load</span> : null}
        </div>
        {error && <p className="field-error" role="alert">{error}</p>}
        {confirming ? (
          <div className="note-photos__confirm">
            <p>Remove this photo? It can't be brought back.</p>
            <div className="note-photos__actions">
              <button type="button" className="btn btn-secondary btn-compact" onClick={() => setConfirming(false)} disabled={busy}>
                Keep it
              </button>
              <button type="button" className="btn btn-danger btn-compact" onClick={remove} disabled={busy}>
                {busy ? 'Removing…' : 'Remove photo'}
              </button>
            </div>
          </div>
        ) : (
          <div className="note-photos__actions">
            <button type="button" className="btn btn-danger btn-compact" onClick={() => setConfirming(true)}>
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
